/**
 * 담당자 기반 필터 Mixin
 * @description cure/List.vue, injection/List.vue에서 공통 사용
 * @module mixins/managerFilterMixin
 *
 * @example
 * import { managerFilterMixin } from '@/mixins/managerFilterMixin';
 *
 * export default {
 *     mixins: [managerFilterMixin],
 *     methods: {
 *         // 반드시 구현해야 하는 메서드
 *         async reloadData() {
 *             await this.init(this.now_date);
 *         }
 *     }
 * }
 *
 * @requires 컴포넌트에서 다음 속성 필요:
 * - this.managerList: Array - 담당자 목록 (MEDICAL_MANAGER_CD, MEDICAL_CD 포함)
 * - this.loginUid: string - 로그인 사용자 UID
 * - this.medical_cd: string - 선택된 치료실 코드
 * - this.now_date: string - 현재 날짜 (YYYYMMDD)
 * - this.reloadData(): Function - 데이터 리로드 함수 (컴포넌트에서 구현)
 */

import { DAY_FILTER_DEFAULTS } from '@/constants/dayFilter';

export const managerFilterMixin = {
    data() {
        return {
            /**
             * 날짜 필터 값 (3일 또는 7일)
             * @type {number}
             */
            dayFilter: DAY_FILTER_DEFAULTS.NON_MANAGER,

            /**
             * 초기 로드 완료 여부 (담당자 기반 기본값 설정용)
             * @type {boolean}
             */
            initialLoadDone: false
        };
    },

    methods: {
        /**
         * 로그인 사용자가 담당자인지 확인하고 기본 필터 설정값 반환
         * @param {Array} managerList - 담당자 목록
         * @param {string} loginUid - 로그인 사용자 UID
         * @returns {Object} { medical_cd, dayFilter, isManager }
         *
         * 2025-02-24: WORK_MEDICAL_CD 기반 매칭으로 변경
         * - 기존: MEDICAL_MANAGER_CD === loginUid (비트마스킹 결과)
         * - 변경: WORK_MEDICAL_CD 컬럼 사용 (명시적 근무지 코드)
         */
        getDefaultFilterByManager(managerList, loginUid) {
            if (!managerList || !loginUid) {
                return {
                    medical_cd: '',
                    dayFilter: DAY_FILTER_DEFAULTS.NON_MANAGER,
                    isManager: false
                };
            }

            // managerList에서 로그인 사용자 찾기
            // MEDICAL_MANAGER_CD는 MEDICAL_MEMBER.UID 값
            const myManager = managerList.find(
                m => m.MEDICAL_MANAGER_CD === loginUid
            );

            // 담당자이고 WORK_MEDICAL_CD가 설정된 경우
            if (myManager && myManager.WORK_MEDICAL_CD) {
                return {
                    medical_cd: myManager.WORK_MEDICAL_CD,
                    dayFilter: DAY_FILTER_DEFAULTS.MANAGER,
                    isManager: true
                };
            }

            // 비담당자: 전체 + 3일
            return {
                medical_cd: '',
                dayFilter: DAY_FILTER_DEFAULTS.NON_MANAGER,
                isManager: false
            };
        },

        /**
         * 담당자 기반 기본값 설정 및 적용
         * @description 첫 로드 시에만 호출됨
         * @returns {boolean} 리로드가 필요한지 여부
         */
        applyDefaultFilterByManager() {
            if (this.initialLoadDone) return false;

            const defaults = this.getDefaultFilterByManager(
                this.managerList,
                this.loginUid
            );

            this.medical_cd = defaults.medical_cd;
            this.dayFilter = defaults.dayFilter;
            this.initialLoadDone = true;

            // 담당자이거나 기본값(3일)이 아닌 경우 리로드 필요
            return defaults.isManager;
        },

        /**
         * 날짜 필터 변경 (공통 로직)
         * @param {number} days - 필터 일수 (3 또는 7)
         */
        async changeDayFilter(days) {
            // 같은 필터면 무시
            if (this.dayFilter === days) return;

            this.dayFilter = days;

            // 한국 시간 당일 날짜 계산 (fallback)
            let targetDate = this.now_date;
            if (!targetDate || targetDate === '' || targetDate === 'Invalid date') {
                targetDate = this.getKoreaDateYYYYMMDD();
                this.now_date = targetDate;
            }

            // 데이터 리로드 (컴포넌트에서 구현)
            await this.reloadData();
        },

        /**
         * 한국 시간 기준 당일 날짜 반환
         * @returns {string} YYYYMMDD 형식
         */
        getKoreaDateYYYYMMDD() {
            const koreaTime = new Date(Date.now() + (9 * 60 * 60 * 1000));
            const year = koreaTime.getUTCFullYear();
            const month = String(koreaTime.getUTCMonth() + 1).padStart(2, '0');
            const day = String(koreaTime.getUTCDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        }
    }
};

export default managerFilterMixin;
